import React, { useMemo, useState } from "react";
import { ArrowRight, Calculator, Check, Zap, AlertTriangle, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
  { key: "basic_soa", label: "Basic SOA", price: 250, blurb: "Standard advice document" },
  { key: "smsf_soa", label: "SMSF SOA", price: 350, blurb: "Self-managed super fund advice" },
  { key: "complex_soa", label: "Complex SOA", price: 450, blurb: "Multi-strategy / multi-entity" },
  { key: "basic_roa", label: "Basic ROA", price: 150, blurb: "Simple fund switch / top-up" },
  { key: "complex_roa", label: "Complex ROA", price: 250, blurb: "Pension commencement / rebalancing" },
];

const PROJECTIONS = [
  { key: "none", label: "None", price: 0, blurb: "No financial modeling" },
  { key: "basic", label: "Basic", price: 50, blurb: "Simple cashflow modeling" },
  { key: "intermediate", label: "Intermediate", price: 100, blurb: "Multi-year scenarios" },
  { key: "complex", label: "Complex", price: 150, blurb: "Tax-effective modeling" },
];

const EXAMPLES = [
  {
    title: "Standard SOA",
    detail: "Super consolidation + standard risk setup",
    inputs: "Basic SOA + Basic Projections",
    turnaround: "Standard (2 Days)",
    quote: 300,
  },
  {
    title: "Basic ROA",
    detail: "Simple fund switch / minor top-up",
    inputs: "Basic ROA + No Projections",
    turnaround: "Standard (24 Hours)",
    quote: 150,
  },
  {
    title: "Complex ROA (Priority)",
    detail: "Urgent pension commencement + rebalancing",
    inputs: "Complex ROA + No Projections",
    turnaround: "Priority (4 Hours), +25% Surcharge",
    quote: 312.5,
  },
  {
    title: "Multi-Entity Restructure",
    detail: "Individual + Trust + SMSF with heavy modeling",
    inputs: "Complex SOA + Intermediate Projections",
    turnaround: "Standard (2 Days)",
    quote: 500,
    capped: true,
  },
];

const billingSteps = [
  { n: "01", title: "Approval First", body: "I deliver the final draft of your SOA, ROA, or Xtools+ projections. You confirm 100% satisfaction before any invoice is issued. No upfront retainers." },
  { n: "02", title: "Transparent AUD Invoicing", body: "A professional digital invoice in Australian Dollars. Flat rate pricing. You always know exactly what you are paying for. No hidden fees or agency markups." },
  { n: "03", title: "Secure 1-Click Payment", body: "Pay securely via PayPal using your corporate card or PayPal account. No friction, no cost, no delay setting up international bank wires." },
  { n: "04", title: "Instant Automated Records", body: "Receive an automated digital receipt immediately upon payment for your practice's tax and accounting records." },
];

const FEE_CAP = 500;

const fmt = (n) => `$${Number(n).toLocaleString("en-AU", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;

const Pricing = () => {
  const [docKey, setDocKey] = useState("basic_soa");
  const [extras, setExtras] = useState(0);
  const [projKey, setProjKey] = useState("none");
  const [priority, setPriority] = useState(false);

  const doc = DOC_TYPES.find((d) => d.key === docKey);
  const proj = PROJECTIONS.find((p) => p.key === projKey);
  const extrasFee = extras * 25;

  const subtotal = doc.price + extrasFee + proj.price;
  const surcharge = priority ? subtotal * 0.25 : 0;
  const rawTotal = subtotal + surcharge;
  const total = Math.min(rawTotal, FEE_CAP);
  const capped = rawTotal > FEE_CAP;

  const turnaround = useMemo(() => {
    const isROA = doc.key.includes("roa");
    if (priority) return isROA ? "4 hours" : "1 business day";
    return isROA ? "24 hours" : "2 business days";
  }, [doc, priority]);

  const lockQuote = () => {
    const summary = {
      service: doc.label,
      extras,
      projection: proj.label,
      priority,
      turnaround,
      subtotal,
      surcharge,
      total,
      capped,
    };
    try {
      sessionStorage.setItem("bp_quote", JSON.stringify(summary));
    } catch (e) {
      // ignore storage failures
    }
    toast.success("Quote locked in. Finish your booking below.");
    document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" });
    // Notify Booking component (same tab — storage event only fires cross-tab)
    window.dispatchEvent(new CustomEvent("bp:quote", { detail: summary }));
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative bg-white border-b-2 border-black"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="max-w-2xl">
          <span className="inline-block bg-[#FACC15] border-2 border-black px-3 py-1 text-xs font-bold uppercase tracking-widest shadow-brutal">
            Transparent Pricing
          </span>
          <h2 className="font-display font-black mt-4 text-4xl md:text-5xl tracking-tighter leading-[1.05]">
            Quote your file. Right here. Right now.
          </h2>
          <p className="mt-5 text-base md:text-lg text-black/75 leading-relaxed">
            The exact fee engine I use behind the scenes. No email back and forth. Pick the document,
            add any extras, choose your turnaround. Fees are flat rate AUD and{" "}
            <strong>hard capped at $500</strong>. No matter how complex.
          </p>
        </div>

        {/* Calculator + Live quote */}
        <div className="mt-12 grid lg:grid-cols-12 gap-6 lg:gap-8" data-testid="fee-calculator">
          {/* Controls */}
          <div className="lg:col-span-7 bg-white border-2 border-black p-6 md:p-8 shadow-brutal-lg">
            <div className="flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              <p className="text-xs font-bold uppercase tracking-widest">Fee Quote Generator</p>
            </div>

            {/* Document Type */}
            <div className="mt-6">
              <label className="block text-xs font-bold uppercase tracking-widest mb-3">
                1. Advice Document Type
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {DOC_TYPES.map((d) => {
                  const selected = d.key === docKey;
                  return (
                    <button
                      type="button"
                      key={d.key}
                      data-testid={`fee-doc-${d.key}`}
                      onClick={() => setDocKey(d.key)}
                      className={`text-left border-2 border-black p-3 transition-all ${
                        selected ? "bg-[#FACC15] shadow-brutal" : "bg-white hover:bg-black/5"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-display font-bold text-base">{d.label}</span>
                        <span className="font-display font-black">${d.price}</span>
                      </div>
                      <p className="text-[11px] text-black/70 mt-1">{d.blurb}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Extras */}
            <div className="mt-7">
              <label className="block text-xs font-bold uppercase tracking-widest mb-3">
                2. Extra Strategies <span className="font-normal normal-case text-black/60">(2 core included, then $25 each)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {[0, 1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    data-testid={`fee-extras-${n}`}
                    onClick={() => setExtras(n)}
                    className={`w-12 h-12 border-2 border-black font-display font-bold text-lg transition-all ${
                      extras === n ? "bg-black text-[#FACC15]" : "bg-white hover:bg-[#FACC15]"
                    }`}
                  >
                    +{n}
                  </button>
                ))}
              </div>
            </div>

            {/* Projections */}
            <div className="mt-7">
              <label className="block text-xs font-bold uppercase tracking-widest mb-3">
                3. Financial Projections
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {PROJECTIONS.map((p) => {
                  const selected = p.key === projKey;
                  return (
                    <button
                      type="button"
                      key={p.key}
                      data-testid={`fee-proj-${p.key}`}
                      onClick={() => setProjKey(p.key)}
                      className={`text-left border-2 border-black p-3 transition-all ${
                        selected ? "bg-[#FACC15] shadow-brutal" : "bg-white hover:bg-black/5"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-display font-bold text-sm">{p.label}</span>
                        <span className="font-display font-bold text-sm">${p.price}</span>
                      </div>
                      <p className="text-[10px] text-black/70 mt-1 leading-tight">{p.blurb}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Turnaround */}
            <div className="mt-7">
              <label className="block text-xs font-bold uppercase tracking-widest mb-3">
                4. Turnaround Time
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="fee-turnaround-standard"
                  onClick={() => setPriority(false)}
                  className={`text-left border-2 border-black p-4 transition-all ${
                    !priority ? "bg-[#FACC15] shadow-brutal" : "bg-white hover:bg-black/5"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <span className="font-display font-bold">Standard</span>
                  </div>
                  <p className="text-[11px] text-black/70 mt-1">SOA: 2 Days · ROA: 24 Hours · No surcharge</p>
                </button>
                <button
                  type="button"
                  data-testid="fee-turnaround-priority"
                  onClick={() => setPriority(true)}
                  className={`text-left border-2 border-black p-4 transition-all ${
                    priority ? "bg-[#FACC15] shadow-brutal" : "bg-white hover:bg-black/5"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    <span className="font-display font-bold">Priority <span className="text-[10px] uppercase tracking-widest bg-black text-white px-1.5 py-0.5 ml-1">+25%</span></span>
                  </div>
                  <p className="text-[11px] text-black/70 mt-1">SOA: 1 Day · ROA: 4 Hours · Surcharge applies</p>
                </button>
              </div>
            </div>
          </div>

          {/* Live quote panel */}
          <aside
            className="lg:col-span-5 bg-black text-white border-2 border-black p-6 md:p-8 shadow-brutal-yellow lg:sticky lg:top-28 self-start"
            data-testid="fee-quote-panel"
          >
            <p className="text-xs font-bold uppercase tracking-widest text-[#FACC15]">Live Quote (AUD)</p>
            <div className="mt-3 flex items-baseline gap-2">
              <span
                className="font-display font-black text-6xl md:text-7xl text-[#FACC15] tracking-tighter leading-none"
                data-testid="fee-total"
              >
                {fmt(total)}
              </span>
              <span className="text-xs font-bold uppercase tracking-widest text-white/70">AUD</span>
            </div>
            {capped && (
              <div
                data-testid="fee-cap-banner"
                className="mt-3 flex items-start gap-2 bg-[#FACC15] text-black border-2 border-[#FACC15] p-3"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <p className="text-xs font-bold leading-snug">
                  $500 FEE CAP APPLIED · Standard calc {fmt(rawTotal)} → capped at {fmt(FEE_CAP)}.
                </p>
              </div>
            )}

            <ul className="mt-6 divide-y divide-white/15 text-sm">
              <li className="py-2.5 flex items-center justify-between">
                <span className="text-white/80">{doc.label}</span>
                <span className="font-bold">{fmt(doc.price)}</span>
              </li>
              <li className="py-2.5 flex items-center justify-between">
                <span className="text-white/80">Extra strategies × {extras}</span>
                <span className="font-bold">{fmt(extrasFee)}</span>
              </li>
              <li className="py-2.5 flex items-center justify-between">
                <span className="text-white/80">Projections, {proj.label}</span>
                <span className="font-bold">{fmt(proj.price)}</span>
              </li>
              <li className="py-2.5 flex items-center justify-between">
                <span className="text-white/80">Subtotal</span>
                <span className="font-bold">{fmt(subtotal)}</span>
              </li>
              <li className="py-2.5 flex items-center justify-between">
                <span className="text-white/80">Priority surcharge (25%)</span>
                <span className="font-bold">{fmt(surcharge)}</span>
              </li>
            </ul>

            <div className="mt-5 border-2 border-[#FACC15] p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#FACC15]">Delivery</p>
              <p className="mt-1 text-sm font-bold">{turnaround}</p>
            </div>

            <button
              type="button"
              onClick={lockQuote}
              data-testid="fee-lock-cta"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#FACC15] text-black border-2 border-[#FACC15] font-bold uppercase text-sm tracking-wide py-4 px-6 hover:bg-white hover:text-black hover:border-white transition-colors"
            >
              Lock This Quote &amp; Book <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-3 text-[11px] text-white/60 leading-relaxed">
              No payment now. <strong className="text-[#FACC15]">Approval first billing</strong>. Invoice
              only after you sign off on the final draft. PayPal in AUD.
            </p>
          </aside>
        </div>

        {/* Real-world examples from the actual Excel sheet */}
        <div className="mt-16">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-widest">Real-world quoting examples</span>
          </div>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {EXAMPLES.map((ex, i) => (
              <div
                key={ex.title}
                data-testid={`fee-example-${i}`}
                className={`relative border-2 border-black p-5 shadow-brutal hover-lift ${
                  ex.capped ? "bg-[#FACC15]" : "bg-white"
                }`}
              >
                {ex.capped && (
                  <span className="absolute -top-3 -right-3 bg-black text-[#FACC15] border-2 border-black px-2 py-1 text-[10px] font-bold uppercase tracking-widest">
                    Cap Applied
                  </span>
                )}
                <p className="text-[10px] font-bold uppercase tracking-widest text-black/60">Example {i + 1}</p>
                <h4 className="font-display font-bold text-lg mt-1">{ex.title}</h4>
                <p className="text-xs text-black/70 mt-2 leading-relaxed">{ex.detail}</p>
                <div className="mt-3 space-y-1 text-[11px] text-black/80">
                  <p><strong className="font-bold">Inputs:</strong> {ex.inputs}</p>
                  <p><strong className="font-bold">Turnaround:</strong> {ex.turnaround}</p>
                </div>
                <p className="mt-3 font-display font-black text-2xl">{fmt(ex.quote)}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-xs text-black/55 max-w-3xl">
            Every quote follows the same logic as my internal SOA &amp; ROA Fee Calculator. Flat AUD pricing,
            transparent surcharges, $500 maximum cap. No hidden retainers, no scope creep invoices.
          </p>
        </div>

        {/* Billing model */}
        <div className="mt-20 md:mt-24" data-testid="billing-model">
          <div className="max-w-2xl">
            <span className="inline-block bg-black text-white border-2 border-black px-3 py-1 text-xs font-bold uppercase tracking-widest">
              Simple, Transparent Billing
            </span>
            <h3 className="font-display font-black mt-4 text-3xl md:text-4xl tracking-tighter leading-[1.05]">
              No retainers. No surprises. Just clean professional invoicing.
            </h3>
          </div>

          <ol className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {billingSteps.map((b) => (
              <li
                key={b.n}
                data-testid={`billing-step-${b.n}`}
                className="bg-white border-2 border-black p-6 shadow-brutal hover-lift"
              >
                <span className="block font-display font-black text-6xl text-[#FACC15] leading-none select-none" aria-hidden>
                  {b.n}
                </span>
                <h4 className="font-display font-bold text-lg mt-3">{b.title}</h4>
                <p className="mt-2 text-sm text-black/75 leading-relaxed">{b.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
